import type { ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import { DEFAULT_LOCALE, resolveSupportedLocale } from '../i18n/locales'

/**
 * Client-side mirror of the nginx Accept-Language redirect.
 *
 * Mounted at every non-localized entry route (e.g. `/`, `/stations/:slug`,
 * `/privacy-policy`) to handle two cases nginx cannot:
 *
 * - **Internal SPA navigation** to a non-localized path (e.g. a hard-coded
 *   `<Link to="/">`) — nginx is bypassed; we re-route in the client.
 * - **SSG fallback** — when nginx is misconfigured and serves the SPA
 *   shell at `/` to a robot, this still emits the right canonical at
 *   the React layer.
 *
 * The chosen locale comes from `navigator.language` via
 * {@link resolveSupportedLocale}; falls back to {@link DEFAULT_LOCALE}.
 */
export function RootLocaleRedirect({
  preserveTail = true,
}: {
  preserveTail?: boolean
}): ReactElement {
  const pathTail = preserveTail && typeof window !== 'undefined' ? window.location.pathname : ''
  const queryHash =
    typeof window !== 'undefined' ? window.location.search + window.location.hash : ''
  const lang = typeof navigator !== 'undefined' ? navigator.language : DEFAULT_LOCALE
  const locale = resolveSupportedLocale(lang)
  const normalizedPath = pathTail === '/' ? '' : pathTail
  return <Navigate to={`/${locale}${normalizedPath}${queryHash}`} replace />
}
